import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

/**
 * Read-only text cell that:
 *
 * <ol>
 *   <li>Truncates with CSS {@code text-overflow: ellipsis} (so narrow viewports get
 *       the usual {@code …}).</li>
 *   <li>Carries a native {@code title} attribute so a hover anywhere in the chip
 *       reveals the FULL untruncated value — works on every browser, no JS, no
 *       collision with PrimeNG tooltips.</li>
 *   <li>Copies the full value to the clipboard on click, with a brief "Copied"
 *       confirmation that fades back into the title after ~1.4 s.</li>
 * </ol>
 *
 * <p>Use it anywhere the rendered string might overflow on small screens — gateway
 * coords, device IDs, IPs, UUIDs, etc. The cursor flips to {@code copy} so the
 * affordance is obvious; the chip ALSO works on touch (tap = copy, same flow as a
 * click) because clipboard API resolves the same on both.
 *
 * <p>The component intentionally renders an inline {@code <span>} so it slots into
 * existing flex/grid layouts without altering line-box height — drop-in replacement
 * for a plain {@code <div class="truncate">…</div>}.
 *
 * @example
 * ```html
 * <lib-copyable-text [text]="gateway.deviceId" class="text-right font-semibold" />
 * ```
 */
@Component({
  selector: 'lib-copyable-text',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'inline-flex min-w-0 max-w-full items-center gap-1',
  },
  template: `
    <span
      class="block min-w-0 cursor-copy truncate"
      [title]="hoverTitle()"
      [attr.aria-label]="ariaLabel()"
      role="button"
      tabindex="0"
      (click)="copy()"
      (keydown.enter)="copy()"
      (keydown.space)="copy()">
      {{ text() }}
    </span>
    @if (justCopied()) {
      <!-- Tiny inline confirmation pill — auto-hides after the timeout fires.
           Lives in the host's flex line so it doesn't push the layout around. -->
      <span
        class="shrink-0 rounded bg-green-100 px-1 py-0.5 text-xs font-bold uppercase tracking-wider text-green-800 dark:bg-green-500/15 dark:text-green-300"
        aria-live="polite">
        {{ copiedLabel() }}
      </span>
    }
  `,
})
export class CopyableTextComponent {
  /** Text rendered AND copied. Defaults to empty string so the host never NPEs. */
  text = input<string>('');

  /**
   * Optional custom hover-title. Defaults to a translated "{text} — click to copy"
   * suffix so the user knows the chip is interactive without a visible button.
   */
  title = input<string | undefined>(undefined);

  protected readonly justCopied = signal(false);
  #transloco = inject(TranslocoService, { optional: true });

  /**
   * Translate or fall back to the English default. Transloco returns the KEY itself
   * when no entry exists for the active language, which would surface raw strings like
   * "shared.copyable.copied" in the UI. We detect that case (output === key) and use
   * the supplied fallback so the component stays presentable in apps that haven't yet
   * imported the shared i18n entries.
   */
  private tr(key: string, fallback: string): string {
    const v = this.#transloco?.translate(key);
    return !v || v === key ? fallback : v;
  }

  protected readonly hoverTitle = computed(() => {
    const explicit = this.title();
    if (explicit) return explicit;
    const txt = this.text();
    if (!txt) return undefined;
    return `${txt} — ${this.tr('shared.copyable.hint', 'click to copy')}`;
  });

  protected readonly copiedLabel = computed(() => this.tr('shared.copyable.copied', 'Copied'));

  protected readonly ariaLabel = computed(() => {
    const txt = this.text();
    if (!txt) return undefined;
    return `${txt}, ${this.tr('shared.copyable.hint', 'click to copy')}`;
  });

  protected async copy(): Promise<void> {
    const value = this.text();
    if (!value) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        // execCommand fallback for browsers without the async API (rare in 2026,
        // but the cost of supporting it is one DOM round-trip).
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      this.justCopied.set(true);
      setTimeout(() => this.justCopied.set(false), 1400);
    } catch {
      /* Silent: clipboard permission denied. The chip stays truncated; the
         user can still hover to read or select-and-copy manually. */
    }
  }
}
